import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from './Dialog';
import { Kbd } from './Kbd';
import { SearchField } from './SearchField';
import './ShortcutSheet.css';

export interface ShortcutItem {
  keys: string[];
  description: string;
  /** How `keys` combine: `'+'` (default) for a chord pressed together (e.g. `Shift`+`Q`), `'/'`
   * for alternatives where any ONE key does it (e.g. the four arrow keys). P2 fix (round 5): every
   * row used to join with `'+'` regardless, so "Move the selection" (four interchangeable arrow
   * keys) read as a nonsensical chord: "↑ + ↓ + ← + →". */
  separator?: '+' | '/';
}

export interface ShortcutSection {
  title: string;
  items: ShortcutItem[];
}

/** DESIGN.md §9.3 `ShortcutSheet` (AC-UI-5: "`?` opens the shortcut sheet"). Generic — a caller
 * supplies its own sections, so any area can reuse this instead of building a bespoke dialog.
 *
 * MVP-272: `title` is a prop rather than a `t()` call, because everything under `ui/` stays
 * dictionary-free so it can render outside an `I18nProvider` (see `ui/SearchField.tsx`'s comment
 * for the rule). MVP-289: `closeLabel` follows it down to `Dialog`'s own header close button for
 * the same reason — this sheet cannot resolve `common.close` on its own behalf.
 *
 * MVP-357: both are required. `title` carried an English `'Keyboard shortcuts'` default and
 * `closeLabel` an implicit one through `Dialog`; both real callers have always passed
 * `t('ops.shortcuts.title')` and `t('common.close')`, so the defaults were unreachable copy that
 * only a new caller could have tripped over — in English, on a Thai screen. */
/**
 * MVP-339: does this item match what the operator typed?
 *
 * Description AND key names, because both are things a person searches by: someone who half-
 * remembers a shortcut types the thing it does ("queue"), and someone looking at a key they just
 * pressed by accident types the key ("shift"). Matching only descriptions would answer the first
 * question and not the second.
 *
 * `toLocaleLowerCase()` rather than `toLowerCase()`: this list renders in Thai by default, and the
 * descriptions in it are Thai sentences. Thai is caseless, so the call is a no-op there — but the
 * KEYS stay Latin in both locales (`Shift`, `Space`), and a Turkish-locale browser lowercasing
 * `I` to `ı` with the invariant `toLowerCase` would silently stop matching them.
 */
function matchesFilter(item: ShortcutItem, needle: string): boolean {
  if (!needle) return true;
  const haystack = [item.description, ...item.keys].join(' ').toLocaleLowerCase();
  return haystack.includes(needle);
}

/**
 * MVP-339: the same sections with non-matching rows dropped, and any section left empty removed
 * entirely — a section title standing over nothing reads as "no shortcuts here", which is a
 * different and wrong claim.
 */
export function filterShortcutSections(sections: ShortcutSection[], query: string): ShortcutSection[] {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return sections;
  return sections
    .map((section) => ({ ...section, items: section.items.filter((item) => matchesFilter(item, needle)) }))
    .filter((section) => section.items.length > 0);
}

export function ShortcutSheet({
  open,
  onClose,
  sections,
  title,
  closeLabel,
  filterLabel,
  filterPlaceholder,
  filterClearLabel,
  noMatchText,
}: {
  open: boolean;
  onClose: () => void;
  sections: ShortcutSection[];
  title: string;
  closeLabel: string;
  /** MVP-339. The filter's accessible name, its placeholder, its clear button's name, and what to
   * say when nothing matches — four props rather than four `useT()` calls, for the same reason
   * `title`/`closeLabel` above are props: `ui/` stays dictionary-free (see `SearchField.tsx`). */
  filterLabel: string;
  filterPlaceholder: string;
  filterClearLabel: string;
  noMatchText: string;
}) {
  const [query, setQuery] = useState('');
  const filterRef = useRef<HTMLInputElement>(null);

  // Reopening the sheet starts fresh. A sheet that remembered the last filter would open showing
  // three of sixteen rows, with the reason sitting in a field the operator has to notice first —
  // the exact "where did everything go" moment this task exists to remove, not add.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    // The sheet is opened by pressing `?`, so the operator's hands are already on the keyboard and
    // the next thing they want to do is type. `Dialog` focuses its own container on open; this runs
    // after that, in the same effect flush, and moves focus one step further in.
    filterRef.current?.focus();
  }, [open]);

  const visible = useMemo(() => filterShortcutSections(sections, query), [sections, query]);

  return (
    <Dialog open={open} onClose={onClose} title={title} closeLabel={closeLabel}>
      <div className="shortcut-sheet">
        <SearchField
          ref={filterRef}
          value={query}
          onChange={setQuery}
          label={filterLabel}
          placeholder={filterPlaceholder}
          clearLabel={filterClearLabel}
          className="shortcut-sheet__filter"
        />
        {visible.length === 0 && <p className="shortcut-sheet__no-match text-meta">{noMatchText}</p>}
        {visible.map((section) => (
          <div key={section.title}>
            <p className="shortcut-sheet__section-title text-label">{section.title}</p>
            <ul className="shortcut-sheet__list">
              {section.items.map((item) => (
                <li key={item.description} className="shortcut-sheet__row">
                  <span className="shortcut-sheet__desc text-body">{item.description}</span>
                  <span className="shortcut-sheet__keys">
                    {item.keys.map((key, i) => (
                      <Fragment key={key}>
                        {i > 0 && <span aria-hidden="true">{item.separator ?? '+'}</span>}
                        <Kbd>{key}</Kbd>
                      </Fragment>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Dialog>
  );
}
